import { useState } from "react";
import { Users as UsersIcon, Music, Coins } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useUsers, TelegramUser } from "@/hooks/useAdminData";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { UserGenerationsDialog } from "@/components/admin/UserGenerationsDialog";

export default function Users() {
  const { data: users, isLoading } = useUsers();
  const [selectedUser, setSelectedUser] = useState<TelegramUser | null>(null);

  return (
    <AdminLayout title="Пользователи" description="Пользователи Telegram бота">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-primary" />
            Все пользователи
          </CardTitle>
          <CardDescription>{users?.length || 0} пользователей</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : !users?.length ? (
            <p className="text-muted-foreground text-center py-8">
              Пока нет пользователей
            </p>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  onClick={() => setSelectedUser(user)}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border/50 cursor-pointer hover:bg-muted/80 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <UsersIcon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {user.username ? `@${user.username}` : user.first_name || 'Пользователь'}
                        {user.last_name && ` ${user.last_name}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        ID: {user.telegram_id} • С {format(new Date(user.created_at), "d MMM yyyy", { locale: ru })}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Coins className="w-3 h-3" />
                      {user.wow_hits_balance} хитов
                    </Badge>
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Music className="w-3 h-3" />
                      {user.total_generations} генераций
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <UserGenerationsDialog
        user={selectedUser}
        open={!!selectedUser}
        onOpenChange={(open) => !open && setSelectedUser(null)}
      />
    </AdminLayout>
  );
}